// What the EP banner should say, as a value — separated from the JSX so it can
// be tested. The suite here is CommonJS node:test with no React renderer, and
// the part worth pinning is the decision, not the markup.
//
// The vocabulary is ink's, word for word (src/components/Overlays.jsx and
// reauthMessage in InkContext.jsx): a person who uses two apps in this fleet
// should not have to learn two vocabularies for one permission boundary.

/**
 * @param {object}  o
 * @param {boolean} o.expired    The session ended under an open tab (a 401 there),
 *        carried across the reload — /auth/status alone cannot tell it from
 *        never having signed in.
 * @param {boolean} o.revoked    /auth/status said epRevoked — the personal grant is gone.
 * @param {object|boolean} o.reauth  /my-permissions `reauth` — the personal grant
 *        is live but no longer covers the DLR.
 * @param {string|null} o.reauthUrl  The platform's link, when it gave one.
 * @param {object|null} o.group  The ACTIVE group, as /api/groups describes it:
 *        `{ id, name, app_authorized, role }`. Null in the personal space.
 * @returns {{kind: string, message: string, href: string|null, label: string|null}|null}
 */
export function epNotice({ expired = false, revoked = false, reauth = null, reauthUrl = null, group = null } = {}) {
  // AN ENDED SESSION OUTRANKS EVERYTHING. With no session there is no grant,
  // realm or group left to know about — only the way back in.
  if (expired) {
    return {
      kind: 'expired',
      message: 'Your Missions session ended. '
        + 'Nothing has been deleted — sign in again to pick up where you left off.',
      href: '/auth/connect',
      label: 'Sign in',
    };
  }

  // The shell reads `reauth` in more than one shape across versions; treat any
  // truthy non-false value as "something is out of step" rather than reading one
  // field name and silently showing nothing when it changes.
  const needsReauth =
    !!reauth && (reauth === true || reauth.required === true || reauth.status === 'required');

  // Explicit false only. /api/groups answers 403-as-empty when discovery is not
  // granted, and a group whose flag is missing is one we know nothing about —
  // guessing "unauthorized" would tell people a working space is broken.
  const groupBlocked = !!group && !!group.id && group.app_authorized === false;

  if (!revoked && !groupBlocked && !needsReauth) return null;

  const obj = typeof reauth === 'object' && reauth ? reauth : null;

  // ORDER: revoked, then the group, then under-covered.
  //
  // A revoked personal grant outranks everything — without it there is no
  // session to be in a group with. A group that has not authorized Missions
  // outranks a personal realm that is merely narrower than the DLR, because in
  // that space NOTHING loads, whereas under-coverage fails only on the parts lost.
  if (revoked) {
    return {
      kind: 'revoked',
      message: 'Missions is no longer connected to your Paradigm account. '
        + 'Nothing has been deleted — it cannot read or save until you reconnect.',
      // The platform's own link when it gave one; otherwise this app's connect
      // route, which is always better than a dead end.
      href: reauthUrl || obj?.reauth_url || '/auth/connect',
      label: 'Reconnect',
    };
  }

  if (groupBlocked) return groupNotice(group);

  return {
    kind: 'reauth',
    message: messageForReason(obj?.reason),
    href: reauthUrl || obj?.reauth_url || '/auth/connect',
    label: 'Re-authorize',
  };
}

// THE GROUP CASE. Paradigm answers GROUP_APP_NOT_AUTHORIZED when the group has no
// active grant for this app — never connected, or revoked, or paused; all three
// read the same (utils/auth.py, get_active_app_eps). Before this, the only sign
// was the raw 403 printed into the missions list, and a person who had
// authorized Missions themselves had no reason to guess the group needed to too.
//
// Only a group admin can grant it (routes/third_party/authorization.py refuses
// anyone else with FORBIDDEN), so the link is offered only when it can work.
// Handing a member a consent page that will refuse them is a dead end dressed as
// an action.
function groupNotice(group) {
  const name = group.name ? `“${group.name}”` : 'This group';
  const base = `${name} hasn’t authorized Missions. Your own access is fine, but a group grants its own access`;
  const href = `/auth/connect-group?group_id=${encodeURIComponent(group.id)}`;

  if (group.role === 'admin') {
    return { kind: 'group', message: `${base} — as a group admin, you can connect it.`, href, label: 'Connect group' };
  }
  if (group.role) {
    // A known, non-admin role: say who can fix it, and offer nothing that fails.
    return { kind: 'group', message: `${base} — ask a group admin to connect it.`, href: null, label: null };
  }
  // Role unknown (an older payload, or the dev spoof list): offer the link, and
  // say plainly who it will work for.
  return { kind: 'group', message: `${base}. A group admin has to connect it.`, href, label: 'Connect group' };
}

function messageForReason(reason) {
  if (reason === 'revoked') return 'Your access has been revoked.';
  if (reason === 'paused') return 'Your access is paused.';
  // dlr_unmet: a realm tightened below what Missions needs, or Missions raised
  // its requirements.
  return 'Missions needs access your current privacy settings no longer grant.';
}
